import useError from 'hooks/useError';
import useSmartlook from 'hooks/useSmartlook';
import {
  useAgreementAcceptanceMutation,
  useApplicationQuery,
  useConfirmApplicationMutation,
  useDigitalSignature,
  useSecciAcceptanceMutation,
} from 'hooks/webapi';
import { useClientQuery } from 'hooks/webapi/client/useClientQuery';
import useNavigateByApplication from 'pages/Waiting/useNavigateByApplication';
import { useCallback, useEffect, useState, useTransition } from 'react';
import {
  ERRORS_TEMPLATES,
  isErrorTemplate,
} from 'utils/errorMessageValidation';

// Number of OTP digit slots shown in the MultiField
const CODE_LENGTH = 4;

export const usePhoneCode = () => {
  const { mutateAsync: acceptSECCI } = useSecciAcceptanceMutation();
  const { mutateAsync: acceptAgreement } = useAgreementAcceptanceMutation();
  const { mutateAsync: confirmApplication } = useConfirmApplicationMutation();
  const { postVereficationCodeToClient, postDigitalSignature } =
    useDigitalSignature();
  const { error } = useSmartlook();
  const { showError } = useError();
  const { refetch: refetchClient } = useClientQuery(false);
  const [isApplicationPolling, setIsApplicationPolling] = useState(false);
  const { data: application } = useApplicationQuery(
    true,
    isApplicationPolling ? 3000 : false,
  );

  const [pending, startPending] = useTransition();
  const [initialLoading, startLoading] = useTransition();
  const { navigateNext } = useNavigateByApplication();

  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [isTimerVisible, setIsTimerVisible] = useState(false);
  const [isWrongCode, setIsWrongCode] = useState(false);

  const resendSms = () => {
    try {
      postVereficationCodeToClient();
    } catch {
      // Do nothing
    } finally {
      setIsTimerVisible(true);
    }
  };

  useEffect(() => {
    startLoading(async () => {
      try {
        await Promise.all([refetchClient(), resendSms()]);
      } catch (e) {
        error('[usePhoneCode][useEffect]', e);
        showError();
      }
    });
  }, []);

  useEffect(() => {
    if (application && application.confirmed) {
      navigateNext(application);
    }
  }, [application, navigateNext]);

  const handleSubmit = useCallback(async () => {
    startPending(async () => {
      try {
        await postDigitalSignature(code.join(''));
      } catch (e) {
        if (isErrorTemplate(e, ERRORS_TEMPLATES.INCORRECT_CODE)) {
          setIsWrongCode(true);
        } else {
          showError();
        }
        return;
      }

      try {
        await acceptSECCI();
        await acceptAgreement();
        await confirmApplication();

        setIsApplicationPolling(true);
      } catch (e) {
        error('[usePhoneCode][Submit]', e);
        showError();
      }
    });
  }, [confirmApplication, code]);

  // All 4 fields must be filled to enable the submit button
  const isCodeComplete = code.every((digit) => digit !== '');

  // Update a single digit slot on MultiField change
  const handleCodeChange = (value: string) => {
    setCode(value.split(''));
    setIsWrongCode(false); // clean wrong code error
  };

  // Reset the code array to empty strings when the user requests a resend
  const handleResend = () => {
    setCode(Array(CODE_LENGTH).fill(''));
    resendSms();
  };

  const handleOnTimeout = () => {
    setIsTimerVisible(false);
  };

  return {
    handleSubmit,
    pending,
    code,
    isCodeComplete,
    handleCodeChange,
    handleResend,
    CODE_LENGTH,
    isTimerVisible,
    handleOnTimeout,
    initialLoading,
    isWrongCode,
    isApplicationPolling,
  };
};
